import { useRef, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  FormHelperText,
  CssBaseline,
  Typography,
  styled,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Link, useLocation } from "react-router-dom";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { LoginStyles } from "../auth/styles/Login.styles";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import CnbLogoMobile from "src/icons/CnbLogoMobile";
import api from "src/store/features/api";
import { LegacyCreateAccountApiArg } from "src/store/features/api/generated";
import { trackEvent } from "src/Amplitude";

export const StyledLinkSpan = styled("a")(() => {
  return {
    color: "#FC4C4C",
    fontSize: "14px",
    textDecoration: "underline",
    cursor: "pointer",
  };
});

export const StyledTextSpan = styled("span")(() => {
  return {
    color: "1F2123",
    fontSize: "14px",
    textDecoration: "none",
  };
});

export default function AccountActivation() {
  const [triggerAccountCreation] = api.useLegacyCreateAccountMutation();
  const search = useLocation().search;
  const emailRaw = new URLSearchParams(search).get("email");
  const email = emailRaw?.replace(" ", "+");
  const oobCode = new URLSearchParams(search).get("oobCode");
  const mounted = useRef(false);
  const classes = LoginStyles();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isMobile = useIsMobile();
  const helperMessage = "Please create a password to continue setting up your C&B Portal account.";
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Must be a valid email").max(255).required("Email is required"),
    password: Yup.string()
      .required("Please Enter your password")
      .min(8, "Must contain 8 characters or more")
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[A-Z])/,
        "Must contain at least one uppercase",
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[a-z])/,
        "Must contain at least one lowercase",
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[0-9])/,
        "Must contain at least one number",
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[$&+,:;=?@#|'<>.^*()%!-\$%\^&\*\-])/,
        "Must contain at least one special character.",
      ),
    newPassword: Yup.string()
      .required("Password cannot be empty")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });
  return (
    <div className={classes.container}>
      <CssBaseline />
      <Grid container className={classes.logoBox}>
        {isMobile ? <CnbLogoMobile height={"200px"} /> : <CalebAndBrownLogo />}
      </Grid>
      {!isSubmitted ? (
        <Grid item xs={12} maxWidth={"559px"}>
          <Typography textAlign={"center"}>{helperMessage}</Typography>
          <Box>
            <Formik
              initialValues={{
                email: email,
                password: "",
                newPassword: "",
                submit: null,
              }}
              onSubmit={async (values, { setErrors, setStatus, setSubmitting }): Promise<void> => {
                const { email, password, newPassword } = values;
                if (password === newPassword) {
                  if (email && oobCode) {
                    let body: LegacyCreateAccountApiArg = {
                      legacyCreateAccountDto: {
                        email,
                        password,
                        oobCode,
                      },
                    };
                    triggerAccountCreation(body)
                      .unwrap()
                      .then(() => {
                        trackEvent(
                          "User Accepted Terms & Conditions",
                          { email },
                          "Legacy Account Activation",
                        );
                        setIsSubmitted(true);
                      })
                      .catch((error) => {
                        let message;
                        if (error.message.includes("already exists")) {
                          message = "This account already exists, please proceed to ";
                        } else if (error?.message?.includes("activation code has expired")) {
                          message = error?.message;
                        } else {
                          message = "Something went wrong";
                        }
                        setErrors({
                          submit: message,
                        });
                        setSubmitting(false);
                        setStatus({ success: false });
                      });
                  }
                }
              }}
              validationSchema={validationSchema}>
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values,
              }): JSX.Element => (
                <Form noValidate onSubmit={handleSubmit}>
                  <TextField
                    InputProps={{
                      disableUnderline: true,
                      className: classes.heightTextField,
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      className: classes.label,
                    }}
                    className={classes.root}
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && errors.email}
                    label="Email"
                    margin="normal"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="email"
                    value={values.email}
                    variant="filled"
                  />
                  <TextField
                    InputProps={{
                      disableUnderline: true,
                      className: classes.heightTextField,
                    }}
                    InputLabelProps={{
                      className: classes.label,
                    }}
                    className={classes.root}
                    error={Boolean(touched.password && errors.password)}
                    fullWidth
                    helperText={touched.password && errors.password}
                    label="Password"
                    margin="normal"
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="password"
                    value={values.password}
                    variant="filled"
                    autoComplete="on"
                  />
                  <TextField
                    InputProps={{
                      disableUnderline: true,
                      className: classes.heightTextField,
                    }}
                    InputLabelProps={{
                      className: classes.label,
                    }}
                    className={classes.root}
                    error={Boolean(touched.newPassword && errors.newPassword)}
                    fullWidth
                    helperText={touched.newPassword && errors.newPassword}
                    label="Confirm Password"
                    margin="normal"
                    name="newPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="password"
                    value={values.newPassword}
                    variant="filled"
                    autoComplete="on"
                  />
                  {errors.submit && (
                    <Box sx={{ mt: 2 }}>
                      {errors.submit.includes("already exist") ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}>
                          <FormHelperText className={classes.errorMessage}>
                            {errors.submit}
                          </FormHelperText>
                          <Link
                            to={"/"}
                            style={{
                              color: "white",
                              textDecoration: "none",
                              fontWeight: "bold",
                              marginLeft: "8px",
                            }}>
                            Login
                          </Link>
                        </div>
                      ) : (
                        <FormHelperText className={classes.errorMessage}>
                          {errors.submit}
                        </FormHelperText>
                      )}
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <StyledTextSpan>
                      {" "}
                      By continuing, you confirm that you have read and agree to our latest{" "}
                      <StyledLinkSpan
                        href="https://www.calebandbrown.com/terms-of-business"
                        target="_blank">
                        Terms of Business
                      </StyledLinkSpan>
                    </StyledTextSpan>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      disableRipple
                      className={classes.button}
                      color="secondary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained">
                      Confirm password
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Grid>
      ) : (
        <>
          <Grid item xs={12} maxWidth={"559px"}>
            <Typography variant="h3" textAlign={"center"} m={2}>
              {"Thank you!"}
            </Typography>
          </Grid>
          <Grid item xs={12} maxWidth={"559px"}>
            <Typography textAlign={"center"}>
              {`To continue setting up your account please check your email inbox for a confirmation email titled "Sign up to Caleb & Brown". `}
            </Typography>
          </Grid>
        </>
      )}
    </div>
  );
}
